import React, { createContext, useContext, useState, useEffect } from 'react';
import moment from 'moment';
import 'moment-timezone';
import { TZ } from '../utils/tz';

const DateRangeContext = createContext();

export const useDateRange = () => {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange должен использоваться внутри DateRangeProvider');
  }
  return context;
};

export const DateRangeProvider = ({ children }) => {
  // По умолчанию устанавливаем текущий месяц
  const [dateRange, setDateRange] = useState([
    moment.tz(TZ).startOf('month'),
    moment.tz(TZ).endOf('month')
  ]);

  // Тип выбранного периода: 'today', 'yesterday', 'week', 'month', 'lastMonth', 'custom'
  const [periodType, setPeriodType] = useState('month');

  // Отладка: проверяем что контекст инициализирован и отслеживаем изменения
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startTz = dateRange[0].clone().tz(TZ).startOf('day');
      const endTz = dateRange[1].clone().tz(TZ).endOf('day');

      console.log('📅 [INIT] DateRangeContext инициализирован/обновлен:', {
        timezone: TZ,
        displayRange: {
          start: dateRange[0].format('YYYY-MM-DD'),
          end: dateRange[1].format('YYYY-MM-DD')
        },
        apiReady: {
          startISO: startTz.toISOString(),
          endISO: endTz.toISOString(),
          startLocal: startTz.format('YYYY-MM-DD HH:mm:ss'),
          endLocal: endTz.format('YYYY-MM-DD HH:mm:ss')
        },
        periodLength: endTz.diff(startTz, 'days') + 1
      });
    }
  }, [dateRange]);

  // Функция для обновления диапазона дат
  const updateDateRange = (newRange) => {
    if (!newRange || !Array.isArray(newRange) || newRange.length !== 2) {
      console.error('⚠️ [VALIDATION] updateDateRange: неверный формат диапазона', newRange);
      return;
    }

    const [start, end] = newRange;
    if (!moment.isMoment(start) || !moment.isMoment(end)) {
      console.error('⚠️ [VALIDATION] updateDateRange: начало или конец не являются moment объектами');
      return;
    }

    // Валидация: start <= end
    if (start.isAfter(end)) {
      console.warn('⚠️ [VALIDATION] updateDateRange: start > end, исправляем порядок');
      setDateRange([end, start]);
      return;
    }

    // Логирование обновления диапазона
    const startTz = start.clone().tz(TZ).startOf('day');
    const endTz = end.clone().tz(TZ).endOf('day');
    const daysDiff = endTz.diff(startTz, 'days');

    console.log('🔄 [DATE_RANGE] updateDateRange вызван:', {
      input: {
        start: start.format('YYYY-MM-DD HH:mm:ss Z'),
        end: end.format('YYYY-MM-DD HH:mm:ss Z')
      },
      normalized: {
        startLocal: startTz.format('YYYY-MM-DD HH:mm:ss'),
        endLocal: endTz.format('YYYY-MM-DD HH:mm:ss'),
        timezone: TZ
      },
      periodInfo: {
        daysDiff: daysDiff + 1,
        startDate: startTz.format('YYYY-MM-DD'),
        endDate: endTz.format('YYYY-MM-DD')
      }
    });

    setDateRange(newRange);
    setPeriodType('custom'); // При ручном выборе дат — устанавливаем custom
  };

  // Функции для быстрого выбора периодов
  const setQuickRange = (type) => {
    const now = moment.tz(TZ);
    let start, end;

    switch (type) {
      case 'today':
        start = now.clone();
        end = now.clone();
        break;
      case 'yesterday':
        start = now.clone().subtract(1, 'days');
        end = now.clone().subtract(1, 'days');
        break;
      case 'week':
        // Последние 7 дней (включая сегодня): от (сегодня - 6 дней) до сегодня
        start = now.clone().subtract(6, 'days');
        end = now.clone();
        break;
      case 'lastWeek':
        start = now.clone().subtract(1, 'weeks').startOf('week');
        end = now.clone().subtract(1, 'weeks').endOf('week');
        break;
      case 'month':
        start = now.clone().startOf('month');
        end = now.clone().endOf('month');
        break;
      case 'lastMonth':
        start = now.clone().subtract(1, 'months').startOf('month');
        end = now.clone().subtract(1, 'months').endOf('month');
        break;
      case 'quarter':
        start = now.clone().startOf('quarter');
        end = now.clone().endOf('quarter');
        break;
      case 'year':
        start = now.clone().startOf('year');
        end = now.clone().endOf('year');
        break;
      default:
        return;
    }

    // Валидация: убеждаемся что start <= end
    if (start.isAfter(end)) {
      console.error('⚠️ [VALIDATION] setQuickRange: start > end для типа:', type);
      [start, end] = [end, start];
    }

    // Логирование расчета периода
    const daysDiff = end.diff(start, 'days');
    const startTz = start.clone().startOf('day');
    const endTz = end.clone().endOf('day');

    console.log('📅 [PERIOD] setQuickRange вызван:', {
      type,
      timezone: TZ,
      now: now.format('YYYY-MM-DD HH:mm:ss Z'),
      calculated: {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
        daysDiff: daysDiff + 1 // +1 потому что включаем оба конца
      },
      forAPI: {
        startISO: startTz.toISOString(),
        endISO: endTz.toISOString(),
        startLocal: startTz.format('YYYY-MM-DD HH:mm:ss'),
        endLocal: endTz.format('YYYY-MM-DD HH:mm:ss'),
        timezone: startTz.format('Z')
      },
      validation: {
        isValid: start.isBefore(end) || start.isSame(end, 'day'),
        startBeforeEnd: start.isBefore(end),
        isSameDay: start.isSame(end, 'day')
      }
    });

    setDateRange([start, end]);
    setPeriodType(type); // Устанавливаем тип периода
  };

  // Получение диапазона в разных форматах
  const getDateRangeFormatted = (format = 'YYYY-MM-DD') => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      console.error('⚠️ [VALIDATION] getDateRangeFormatted: dateRange не инициализирован');
      const fallback = moment.tz(TZ);
      const fallbackStart = fallback.clone().startOf('day');
      const fallbackEnd = fallback.clone().endOf('day');
      return {
        start: fallbackStart.format(format),
        end: fallbackEnd.format(format),
        startISO: fallbackStart.toISOString(),
        endISO: fallbackEnd.toISOString()
      };
    }

    // Нормализуем границы к началу/концу суток в нужном TZ,
    // чтобы одиночный день корректно покрывал весь день.
    const startTz = dateRange[0].clone().tz(TZ).startOf('day');
    const endTz = dateRange[1].clone().tz(TZ).endOf('day');

    const result = {
      start: startTz.format(format),
      end: endTz.format(format),
      startISO: startTz.toISOString(),
      endISO: endTz.toISOString()
    };

    // Логирование форматирования для API
    console.log('🔧 [API_FORMAT] getDateRangeFormatted:', {
      input: {
        rawStart: dateRange[0].format('YYYY-MM-DD HH:mm:ss Z'),
        rawEnd: dateRange[1].format('YYYY-MM-DD HH:mm:ss Z')
      },
      normalized: {
        startLocal: startTz.format('YYYY-MM-DD HH:mm:ss'),
        endLocal: endTz.format('YYYY-MM-DD HH:mm:ss'),
        timezone: startTz.format('Z'),
        timezoneName: TZ
      },
      forAPI: {
        startISO: result.startISO,
        endISO: result.endISO,
        startDate: result.start,
        endDate: result.end
      },
      validation: {
        isValid: startTz.isBefore(endTz) || startTz.isSame(endTz, 'day'),
        daysCovered: endTz.diff(startTz, 'days') + 1,
        startIsStartOfDay: startTz.hour() === 0 && startTz.minute() === 0 && startTz.second() === 0,
        endIsEndOfDay: endTz.hour() === 23 && endTz.minute() === 59 && endTz.second() === 59
      }
    });

    return result;
  };

  const value = {
    dateRange,
    periodType,
    updateDateRange,
    setQuickRange,
    getDateRangeFormatted
  };

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  );
};
